import React, { useEffect, useState } from "react";
import {
  CompanyDto,
  CompanyContactDto,
  CompanyRequisitesDto,
  createCompany,
  updateCompany,
  getCompanyRequisites,
  saveCompanyRequisites,
  deleteCompany,
} from "../../api/companies";
import { getRole } from "../../hooks/usePermissions";
import { CompanySettlementsTab } from "./CompanySettlementsTab";

import "./styles/company-modal.css";

const ALL_ROLES = [
  "supplier",
  "customer",
  "partner",
  "reseller",
  "manufacturer",
] as const;

type TabKey = "main" | "addresses" | "requisites" | "settlements";

interface CompanyModalProps {
  company: CompanyDto | null;
  onClose: () => void;
}

export function CompanyModal({ company, onClose }: CompanyModalProps) {
  const isNew = !company?.id;

  // Основные поля
  const [name, setName] = useState(company?.name ?? "");
  const [bin, setBin] = useState(company?.bin ?? "");
  const [phone, setPhone] = useState(company?.phone ?? "");
  const [email, setEmail] = useState(company?.email ?? "");
  const [director, setDirector] = useState(company?.director ?? "");
  const [roles, setRoles] = useState<string[]>(company?.roles ?? []);
  const [contacts, setContacts] = useState<CompanyContactDto[]>(
    company?.contacts ?? []
  );

  // Реквизиты
  const [requisites, setRequisites] = useState<CompanyRequisitesDto | null>(
    null
  );
  const [requisitesLoaded, setRequisitesLoaded] = useState(false);
  const [requisitesLoading, setRequisitesLoading] = useState(false);

  const [tab, setTab] = useState<TabKey>("main");

  // При смене компании
  useEffect(() => {
    setName(company?.name ?? "");
    setBin(company?.bin ?? "");
    setPhone(company?.phone ?? "");
    setEmail(company?.email ?? "");
    setDirector(company?.director ?? "");
    setRoles(company?.roles ?? []);
    setContacts(company?.contacts ?? []);

    setRequisites(null);
    setRequisitesLoaded(false);
    setRequisitesLoading(false);
    setTab("main");
  }, [company]);

  // Ленивая загрузка реквизитов
  useEffect(() => {
    if (tab === "requisites" || tab === "addresses") {
      if (!requisitesLoaded && company?.id && !requisitesLoading) {
        loadRequisites(company.id);
      } else if (!company?.id && !requisitesLoaded) {
        setRequisites({
          addressLegal: "",
          addressActual: "",
          bankName: "",
          bankBik: "",
          bankAccount: "",
          kbe: "",
        });
        setRequisitesLoaded(true);
      }
    }
  }, [tab]);

  async function loadRequisites(companyId: number) {
    try {
      setRequisitesLoading(true);
      const data = await getCompanyRequisites(companyId);

      if (data) {
        setRequisites(data);
      } else {
        setRequisites({
          addressLegal: "",
          addressActual: "",
          bankName: "",
          bankBik: "",
          bankAccount: "",
          kbe: "",
        });
      }

      setRequisitesLoaded(true);
    } finally {
      setRequisitesLoading(false);
    }
  }

  // Роли
  function toggleRole(role: string) {
    if (roles.includes(role)) {
      setRoles(roles.filter((r) => r !== role));
    } else {
      setRoles([...roles, role]);
    }
  }

  // Контакты
  function addContact() {
    setContacts([
      ...contacts,
      {
        name: "",
        phone: "",
        email: "",
        position: "",
        isMain: contacts.length === 0,
      },
    ]);
  }

  function updateContact(index: number, patch: Partial<CompanyContactDto>) {
    const copy = [...contacts];
    copy[index] = { ...copy[index], ...patch };
    setContacts(copy);
  }

  function removeContact(index: number) {
    const copy = [...contacts];
    copy.splice(index, 1);
    setContacts(copy);
  }

  function setMainContact(index: number) {
    const copy = contacts.map((c, i) => ({
      ...c,
      isMain: i === index,
    }));
    setContacts(copy);
  }

  // Сохранение
  async function handleSave() {
  // 1. Чистим контакты — удаляем id, если он undefined
  const cleanedContacts = contacts.map((c) => {
    const copy: any = { ...c };
    if (copy.id === undefined) delete copy.id;
    return copy;
  });

  // 2. Формируем payload
  const payload: Partial<CompanyDto> = {
    name,
    bin,
    phone,
    email,
    director,
    roles,
    contacts: cleanedContacts,
  };

  // 3. Сохраняем компанию
  let saved: CompanyDto;

  if (isNew) {
    saved = await createCompany(payload);
  } else {
    saved = await updateCompany(company!.id, payload);
  }

  // 4. Сохраняем реквизиты (если загружены)
  if (requisitesLoaded && requisites) {
    await saveCompanyRequisites(saved.id, requisites);
  }

  onClose();
}


  // Рендер ролей
  function renderRolesSelector() {
    return (
      <div className="company-form-full">
        <label className="field-label">Роли</label>
        <div className="roles-badges-row">
          {ALL_ROLES.map((role) => {
            const active = roles.includes(role);
            return (
              <button
                key={role}
                type="button"
                className={`role-badge-toggle ${active ? "active" : ""}`}
                onClick={() => toggleRole(role)}
              >
                {role[0].toUpperCase() + role.slice(1)}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // Вкладка "Основное"
  function renderMainTab() {
    return (
      <div className="company-form-grid">
        <div className="company-form-full">
          <label className="field-label">Название</label>
          <input
            className="field-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label">БИН</label>
          <input
            className="field-input"
            value={bin}
            onChange={(e) => setBin(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label">Телефон</label>
          <input
            className="field-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label">Email</label>
          <input
            className="field-input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>

        <div>
          <label className="field-label">Директор</label>
          <input
            className="field-input"
            value={director}
            onChange={(e) => setDirector(e.target.value)}
          />
        </div>

        {renderRolesSelector()}

        <div className="company-form-full">
          <div className="contacts-header-row">
            <span className="field-label">Контакты</span>
            <button type="button" className="btn-edit" onClick={addContact}>
              Добавить контакт
            </button>
          </div>

          <table className="users-table">
            <thead>
              <tr>
                <th>Имя</th>
                <th>Телефон</th>
                <th>Email</th>
                <th>Должность</th>
                <th>Основной</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {contacts.map((c, index) => (
                <tr key={index}>
                  <td>
                    <input
                      className="field-input"
                      value={c.name}
                      onChange={(e) =>
                        updateContact(index, { name: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={c.phone}
                      onChange={(e) =>
                        updateContact(index, { phone: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={c.email}
                      onChange={(e) =>
                        updateContact(index, { email: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      className="field-input"
                      value={c.position}
                      onChange={(e) =>
                        updateContact(index, { position: e.target.value })
                      }
                    />
                  </td>
                  <td>
                    <input
                      type="radio"
                      checked={c.isMain}
                      onChange={() => setMainContact(index)}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn-edit"
                      onClick={() => removeContact(index)}
                    >
                      Удалить
                    </button>
                  </td>
                </tr>
              ))}

              {contacts.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ padding: "12px 16px", fontSize: 14 }}>
                    Контактов пока нет
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Вкладка "Адреса"
  function renderAddressesTab() {
    if (!requisitesLoaded || !requisites) {
      return <div>Загрузка реквизитов...</div>;
    }

    return (
      <div className="company-form-grid">
        <div className="company-form-full">
          <label className="field-label">Юридический адрес</label>
          <input
            className="field-input"
            value={requisites.addressLegal}
            onChange={(e) =>
              setRequisites({ ...requisites, addressLegal: e.target.value })
            }
          />
        </div>

        <div className="company-form-full">
          <label className="field-label">Фактический адрес</label>
          <input
            className="field-input"
            value={requisites.addressActual}
            onChange={(e) =>
              setRequisites({ ...requisites, addressActual: e.target.value })
            }
          />
        </div>
      </div>
    );
  }

  // Вкладка "Реквизиты"
  function renderRequisitesTab() {
    if (!requisitesLoaded || !requisites) {
      return <div>Загрузка реквизитов...</div>;
    }

    return (
      <div className="company-form-grid">
        <div>
          <label className="field-label">Банк</label>
          <input
            className="field-input"
            value={requisites.bankName}
            onChange={(e) =>
              setRequisites({ ...requisites, bankName: e.target.value })
            }
          />
        </div>

        <div>
          <label className="field-label">БИК</label>
          <input
            className="field-input"
            value={requisites.bankBik}
            onChange={(e) =>
              setRequisites({ ...requisites, bankBik: e.target.value })
            }
          />
        </div>

        <div>
          <label className="field-label">Расчётный счёт</label>
          <input
            className="field-input"
            value={requisites.bankAccount}
            onChange={(e) =>
              setRequisites({ ...requisites, bankAccount: e.target.value })
            }
          />
        </div>

        <div>
          <label className="field-label">КБЕ</label>
          <input
            className="field-input"
            value={requisites.kbe}
            onChange={(e) =>
              setRequisites({ ...requisites, kbe: e.target.value })
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="company-modal-overlay">
      <div className="company-modal">
        <div className="company-modal-header">
          <h2>{isNew ? "Новая компания" : name || "Компания"}</h2>
          <div className="company-modal-close" onClick={onClose}>
            ×
          </div>
        </div>

        <div className="company-tabs">
          <div
            className={`company-tab ${tab === "main" ? "active" : ""}`}
            onClick={() => setTab("main")}
          >
            Основное
          </div>
          <div
            className={`company-tab ${tab === "addresses" ? "active" : ""}`}
            onClick={() => setTab("addresses")}
          >
            Адреса
          </div>
          <div
            className={`company-tab ${tab === "requisites" ? "active" : ""}`}
            onClick={() => setTab("requisites")}
          >
            Реквизиты
          </div>
          {!isNew && (
            <div
              className={`company-tab ${tab === "settlements" ? "active" : ""}`}
              onClick={() => setTab("settlements")}
            >
              Взаиморасчёты
            </div>
          )}
        </div>

        <div className="company-modal-body">
          {tab === "main" && renderMainTab()}
          {tab === "addresses" && renderAddressesTab()}
          {tab === "requisites" && renderRequisitesTab()}
          {tab === "settlements" && company && (
            <CompanySettlementsTab
              company={company}
              onUpdated={() => {}}
            />
          )}
        </div>

        <div className="company-modal-footer">
          {!isNew && getRole() === "ADMIN" && (
            <button
              className="btn-edit"
              style={{ color: "#ef4444", marginRight: "auto" }}
              onClick={async () => {
                if (!window.confirm(`Удалить компанию «${company?.name}»? Это действие нельзя отменить.`)) return;
                try {
                  await deleteCompany(company!.id as number);
                  onClose();
                } catch (e: any) {
                  alert(e?.response?.data?.message ?? "Не удалось удалить компанию");
                }
              }}
            >
              🗑 Удалить
            </button>
          )}
          <button className="btn-edit" onClick={onClose}>
            Отмена
          </button>
          <button className="btn-primary" onClick={handleSave}>
            Сохранить
          </button>
        </div>
      </div>
    </div>
  );
}